import React, { useState } from "react";
import { BellRing, Loader2, CheckCircle2, Send } from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { FIELDS, LEVELS, COUNTRIES } from "../data/courses";
import { matchScholarships } from "../data/scholarships";

export default function ScholarshipAlert({ defaults = {} }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", email: "",
    field: defaults.field || "Any",
    level: defaults.level || "Any",
    country: defaults.country || "Any",
  });
  const [state, setState] = useState("idle");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.post("/scholarship-alerts", {
        ...form,
        scholarships: matchScholarships({ field: form.field, level: form.level, country: form.country }),
      });
      // remember subscription locally for the Saved Dashboard
      localStorage.setItem("cc_alert", JSON.stringify({ ...form, at: new Date().toISOString() }));
      setState("sent");
      toast({ title: "Alerts activated!", description: "We've emailed you a confirmation." });
      setTimeout(() => setState("idle"), 3000);
    } catch (err) {
      setState("idle");
      toast({ title: "Could not subscribe", description: "Please try again.", variant: "destructive" });
    }
  };

  return (
    <div className="rounded-2xl bg-white cc-shadow-card p-6 md:p-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-[#F58220]/10 text-[#F58220] flex items-center justify-center">
          <BellRing size={22} />
        </div>
        <div>
          <h3 className="font-head text-lg font-bold text-[#16244F]">Get Scholarship Alerts</h3>
          <p className="text-sm text-[#5a6274]">We'll email you when new scholarships match your profile.</p>
        </div>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input required placeholder="Your name" value={form.name} onChange={(e) => set("name", e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none" />
        <input required type="email" placeholder="Your email" value={form.email} onChange={(e) => set("email", e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none" />
        <select value={form.field} onChange={(e) => set("field", e.target.value)} className="px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none">
          <option value="Any">Any field</option>
          {FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={form.level} onChange={(e) => set("level", e.target.value)} className="px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none">
          <option value="Any">Any level</option>
          {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={form.country} onChange={(e) => set("country", e.target.value)} className="sm:col-span-2 px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none">
          <option value="Any">Any destination</option>
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="submit" disabled={state !== "idle"}
          className="sm:col-span-2 w-full inline-flex items-center justify-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold py-3.5 rounded-lg transition-colors disabled:opacity-80">
          {state === "sending" ? (<><Loader2 size={18} className="animate-spin" /> Subscribing...</>)
            : state === "sent" ? (<><CheckCircle2 size={18} /> Subscribed!</>)
            : (<><Send size={18} /> Notify Me</>)}
        </button>
      </form>
    </div>
  );
}
